import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Building2, DollarSign, MapPin, AlertCircle, FileText, Pill,
  Stethoscope, Activity, TrendingDown, TrendingUp, Search,
  Package, Hash, ChevronRight, Loader2, Shield, AlertTriangle, Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SearchBarWithAutocomplete } from '@/components/SearchBarWithAutocomplete';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchResultsProceduresCarousel } from '@/components/SearchResultsProceduresCarousel';
import { useBrand } from '@/config/brands';
import { ConsumerSearch } from '@/components/ConsumerSearch';
import { Helmet } from "react-helmet-async";
import { trackSearch, trackSearchResultClick } from "@/utils/analytics";
import { useTranslation } from 'react-i18next';

interface HospitalResult {
  id: string;
  name: string;
  city: string;
  state: string;
  total_procedures: number;
  avg_markup_percentage?: number | null;
  avg_gross_markup_percentage?: number | null;
  // Median-baseline markup — the hospital-level headline metric (rank basis)
  median_gross_markup_percentage?: number | null;
  shame_score?: number | null;
  shame_score_calculated?: number | null;
  data_quality_score?: number | null;
  price_outlier_percentage?: number | null;
  outlier_procedures_count?: number | null;
  outlier_expensive_count?: number | null;
  worst_overcharge_description?: string | null;
  worst_overcharge_amount?: number | null;
  worst_overcharge_markup?: number | null;
  global_rank?: number;
  shame_rank?: number;
  total_hospitals?: number;
  cpt_avg_markup_percentage?: number | null;
  drg_avg_markup_percentage?: number | null;
  rev_avg_markup_percentage?: number | null;
  ndc_avg_markup_percentage?: number | null;
  cpt_procedure_count?: number | null;
  drg_procedure_count?: number | null;
  rev_procedure_count?: number | null;
  ndc_procedure_count?: number | null;
}

interface SearchResult {
  id: string;
  code: string;
  codeType: 'CPT' | 'DRG' | 'REV' | 'NDC' | 'HCPCS' | 'SUPPLY' | 'DRUG' | 'OTHER';
  description: string;
  consumerFriendlyName?: string;
  matchCount: number;
  avgPrice?: number;
  minPrice?: number;
  maxPrice?: number;
  minHospitalId?: string;
  minHospitalName?: string;
  maxHospitalId?: string;
  maxHospitalName?: string;
  hospitalCount: number;
  source: string;
  standardUnit?: string;
  avgPricePerUnit?: number;
  priceSpread?: number;
}

interface CategoryMatch {
  category: string;
  displayName: string;
  icon: React.ComponentType<any>;
  matchCount: number;
  results: SearchResult[];
  color: string;
  loading: boolean;
  error?: string;
}

const SearchEnhanced = () => {
  const brand = useBrand();
  // AHP brand gets its own static-first search page — return early before any DB hooks
  if (brand.key === 'ahp') {
    return <ConsumerSearch />;
  }
  return <SearchEnhancedInner />;
};

const SearchEnhancedInner = () => {
  const { t } = useTranslation('search');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get('q') || '';
  const typeParam = searchParams.get('type') || 'hospital';

  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [searchType, setSearchType] = useState<'hospital' | 'procedure'>(
    typeParam as 'hospital' | 'procedure'
  );
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryMatches, setCategoryMatches] = useState<CategoryMatch[]>([]);
  const [hospitalResults, setHospitalResults] = useState<HospitalResult[]>([]);
  const [isSearchingHospitals, setIsSearchingHospitals] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50; // Show top 50 worst offenders initially

  // Category definitions
  const categories = [
    {
      key: 'cpt',
      displayName: t('categories.cptHcpcsCodes'),
      icon: Stethoscope,
      color: 'bg-blue-500',
      searchField: 'cpt_code',
      table: 'chargemaster_mappings',
      description: 'Current Procedural Terminology and HCPCS codes'
    },
    {
      key: 'drg',
      displayName: t('categories.drgCodes'),
      icon: Building2,
      color: 'bg-purple-500',
      searchField: 'drg_code',
      table: 'chargemaster_mappings',
      description: 'Diagnosis-Related Groups'
    },
    {
      key: 'rev',
      displayName: t('categories.revenueCodes'),
      icon: Hash,
      color: 'bg-green-500',
      searchField: 'revenue_code',
      table: 'chargemaster_mappings',
      description: 'Revenue center codes'
    },
    {
      key: 'ndc',
      displayName: t('categories.ndcCodes'),
      icon: Pill,
      color: 'bg-orange-500',
      searchField: 'ndc_code',
      table: 'chargemaster_mappings',
      description: 'National Drug Codes'
    },
    {
      key: 'supply',
      displayName: t('categories.medicalSupplies'),
      icon: Package,
      color: 'bg-cyan-500',
      searchField: 'supply_code',
      table: 'chargemaster_mappings',
      description: 'Medical supplies and equipment'
    },
    {
      key: 'drug',
      displayName: t('categories.drugsMedications'),
      icon: Pill,
      color: 'bg-red-500',
      searchField: 'drug_code',
      table: 'chargemaster_mappings',
      description: 'Prescription drugs and medications'
    }
  ];

  // Search based on type when query changes
  useEffect(() => {
    if (!searchQuery) return;
    
    // Add a small delay to debounce user typing
    const timer = setTimeout(() => {
      if (searchType === 'procedure') {
        performComprehensiveSearch(searchQuery);
      } else {
        searchHospitals(searchQuery);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, searchType]);

  // Update URL when search changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (searchType) params.set('type', searchType);
    setSearchParams(params);
  }, [searchQuery, searchType]);

  const searchHospitals = async (query: string) => {
    setIsSearchingHospitals(true);
    try {
      // Get ALL rankings data first to calculate global rankings (including category breakdowns).
      // Filters out exclude_from_rankings so dedup'd dups, bad-chargemaster hospitals,
      // and unverifiable rows don't appear in search results — must mirror /hospital-rankings.
      const { data: allRankingsData, error: allRankingsError } = await supabase
        .from('hospital_ranking_summary')
        .select(`
          hospital_id,
          avg_gross_markup_percentage,
          median_gross_markup_percentage,
          shame_score_calculated,
          total_procedures,
          expensive_procedures_count,
          outlier_expensive_count,
          cpt_avg_markup_percentage,
          drg_avg_markup_percentage,
          rev_avg_markup_percentage,
          ndc_avg_markup_percentage,
          cpt_procedure_count,
          drg_procedure_count,
          rev_procedure_count,
          ndc_procedure_count,
          exclude_from_rankings
        `)
        .not('avg_gross_markup_percentage', 'is', null)
        .or('exclude_from_rankings.is.null,exclude_from_rankings.eq.false')
        .order('median_gross_markup_percentage', { ascending: false, nullsFirst: false });
        
      if (allRankingsError) throw allRankingsError;
      if (!allRankingsData || allRankingsData.length === 0) {
        setHospitalResults([]);
        return;
      }

      // Create a mapping of hospital_id to global rank
      const globalRankings = new Map();
      allRankingsData.forEach((hospital, index) => {
        globalRankings.set(hospital.hospital_id, {
          rank: index + 1,
          totalHospitals: allRankingsData.length,
          ...hospital
        });
      });

      // Use join to get hospitals with rankings that match the search.
      // Tokenize the query on whitespace and AND the tokens (each matched across
      // name/city/state) so a query like "Texas Health Kaufman" resolves "Texas
      // Health Presbyterian Hospital Kaufman" even though the words aren't
      // contiguous. Chained .or() calls AND together in PostgREST, so every
      // token must appear in one of the three fields.
      let hospitalQuery = supabase
        .from('hospitals')
        .select(`
          *,
          hospital_ranking_summary!inner(
            avg_gross_markup_percentage,
            median_gross_markup_percentage,
            shame_score_calculated,
            total_procedures,
            expensive_procedures_count,
            outlier_expensive_count,
            cpt_avg_markup_percentage,
            drg_avg_markup_percentage,
            rev_avg_markup_percentage,
            ndc_avg_markup_percentage,
            cpt_procedure_count,
            drg_procedure_count,
            rev_procedure_count,
            ndc_procedure_count
          )
        `);
      for (const token of query.trim().split(/\s+/)) {
        const t = token.replace(/[%,()]/g, ''); // strip PostgREST filter metachars (comma/paren break .or() parsing)
        if (t) hospitalQuery = hospitalQuery.or(`name.ilike.%${t}%,city.ilike.%${t}%,state.ilike.%${t}%`);
      }
      const { data: joinedData, error: joinedError } = await hospitalQuery
        .not('hospital_ranking_summary.avg_gross_markup_percentage', 'is', null)
        .limit(50);

      const hospitalsData = joinedData;

      if (joinedError) throw joinedError;
      if (!hospitalsData) {
        setHospitalResults([]);
        return;
      }

      // Transform the joined data and add global rank lookup
      const mergedResults = hospitalsData.map(hospital => {
        const ranking = hospital.hospital_ranking_summary[0]; // Get the joined ranking data
        const globalRanking = globalRankings.get(hospital.id);

        return {
          ...hospital,
          avg_gross_markup_percentage: ranking?.avg_gross_markup_percentage || 0,
          median_gross_markup_percentage: ranking?.median_gross_markup_percentage ?? null,
          shame_score_calculated: ranking?.shame_score_calculated || 0,
          total_procedures_calculated: ranking?.total_procedures || 0,
          expensive_procedures_count: ranking?.expensive_procedures_count || 0,
          outlier_expensive_count: ranking?.outlier_expensive_count || 0,
          global_rank: globalRanking?.rank || 0,
          total_hospitals: globalRanking?.totalHospitals || 0,
          // Category-specific markup data from joined ranking
          cpt_avg_markup_percentage: ranking?.cpt_avg_markup_percentage,
          drg_avg_markup_percentage: ranking?.drg_avg_markup_percentage,
          rev_avg_markup_percentage: ranking?.rev_avg_markup_percentage,
          ndc_avg_markup_percentage: ranking?.ndc_avg_markup_percentage,
          cpt_procedure_count: ranking?.cpt_procedure_count || 0,
          drg_procedure_count: ranking?.drg_procedure_count || 0,
          rev_procedure_count: ranking?.rev_procedure_count || 0,
          ndc_procedure_count: ranking?.ndc_procedure_count || 0,
        };
      });

      // Sort by global rank (ascending - so rank 1 comes first)
      mergedResults.sort((a, b) => (a.global_rank || 0) - (b.global_rank || 0));
      
      setHospitalResults(mergedResults);
    } catch (error) {
      console.error('Hospital search error:', error);
      setHospitalResults([]);
    } finally {
      setIsSearchingHospitals(false);
    }
  };

  const performComprehensiveSearch = async (query: string) => {
    const upperQuery = query.toUpperCase().trim();
    
    // Initialize category matches
    const matches: CategoryMatch[] = categories.map(cat => ({
      category: cat.key,
      displayName: cat.displayName,
      icon: cat.icon,
      matchCount: 0,
      results: [],
      color: cat.color,
      loading: true,
      error: undefined
    }));
    
    setCategoryMatches(matches);

    // Search each category in parallel
    const searchPromises = categories.map(async (cat, index) => {
      try {
        let results: SearchResult[] = [];
        
        if (cat.table === 'chargemaster_mappings') {
          let data: any = null;
          let error: any = null;

          // First try exact match on code field (fast, indexed)
          const { data: exactData, error: exactError } = await (supabase as any)
            .from('chargemaster_mappings')
            .select('id, cpt_code, drg_code, revenue_code, ndc_code, supply_code, drug_code, chargemaster_description, gross_charge, averaged_price, min_negotiated_rate, max_negotiated_rate, hospital_id')
            .eq(cat.searchField, upperQuery)
            .limit(50);

          if (exactData && exactData.length > 0) {
            data = exactData;
          } else {
            // Fall back to LIKE search only if no exact matches
            const searchTerms = [];

            // Search the code field with LIKE
            searchTerms.push(`${cat.searchField}.ilike.%${upperQuery}%`);

            // Add description searches only for shorter queries to avoid timeout
            if (query.length >= 4) {
              searchTerms.push(`chargemaster_description.ilike.%${query}%`);
            }

            const { data: likeData, error: likeError } = await (supabase as any)
              .from('chargemaster_mappings')
              .select('id, cpt_code, drg_code, revenue_code, ndc_code, supply_code, drug_code, chargemaster_description, gross_charge, averaged_price, min_negotiated_rate, max_negotiated_rate, hospital_id')
              .or(searchTerms.join(','))
              .not(cat.searchField, 'is', null)
              .limit(30);

            data = likeData;
            error = likeError;
          }

          if (error) throw error;

          // chargemaster_mappings has no FK to hospitals (dropped for bulk
          // imports), so PostgREST can't embed hospitals(name) — resolve
          // names with a second query over the few distinct ids instead.
          const hospitalNames: Record<string, string> = {};
          const hospitalIds = [...new Set((data || []).map((r: any) => r.hospital_id).filter(Boolean))] as string[];
          if (hospitalIds.length > 0) {
            const { data: hospRows } = await supabase
              .from('hospitals')
              .select('id, name')
              .in('id', hospitalIds);
            for (const h of hospRows || []) hospitalNames[h.id] = h.name;
          }

          // Group by code and aggregate
          const codeMap = new Map<string, SearchResult>();

          (data || []).forEach(item => {
            const code = item[cat.searchField];
            if (!code) return;

            const existing = codeMap.get(code);
            const price = item.gross_charge || item.averaged_price || 0;
            
            if (existing) {
              existing.matchCount++;
              existing.hospitalCount++;
              if (price > 0) {
                // Update average price
                const currentTotal = (existing.avgPrice || 0) * (existing.matchCount - 1);
                existing.avgPrice = (currentTotal + price) / existing.matchCount;

                // Track min price hospital
                if (!existing.minPrice || price < existing.minPrice) {
                  existing.minPrice = price;
                  existing.minHospitalId = item.hospital_id;
                  existing.minHospitalName = hospitalNames[item.hospital_id] || t('results.unknownHospital');
                }

                // Track max price hospital
                if (!existing.maxPrice || price > existing.maxPrice) {
                  existing.maxPrice = price;
                  existing.maxHospitalId = item.hospital_id;
                  existing.maxHospitalName = hospitalNames[item.hospital_id] || t('results.unknownHospital');
                }

                // Calculate price spread
                if (existing.minPrice && existing.maxPrice && existing.minPrice > 0) {
                  existing.priceSpread = ((existing.maxPrice - existing.minPrice) / existing.minPrice) * 100;
                }
              }
            } else {
              codeMap.set(code, {
                id: code,
                code: code,
                codeType: cat.key.toUpperCase() as any,
                description: item.chargemaster_description || '',
                matchCount: 1,
                avgPrice: price > 0 ? price : undefined,
                minPrice: price > 0 ? price : undefined,
                maxPrice: price > 0 ? price : undefined,
                minHospitalId: price > 0 ? item.hospital_id : undefined,
                minHospitalName: price > 0 ? (hospitalNames[item.hospital_id] || t('results.unknownHospital')) : undefined,
                maxHospitalId: price > 0 ? item.hospital_id : undefined,
                maxHospitalName: price > 0 ? (hospitalNames[item.hospital_id] || t('results.unknownHospital')) : undefined,
                hospitalCount: 1,
                source: cat.displayName,
                priceSpread: 0
              });
            }
          });

          results = Array.from(codeMap.values());
        }

        // Also check pricing averages tables for CPT/HCPCS codes
        if (cat.key === 'cpt') {
          try {
            let avgData = null;
            let avgError = null;

            // First try exact match on procedure_code (fast, indexed)
            const { data: exactAvgData, error: exactAvgError } = await supabase
              .from('procedure_pricing_averages')
              .select(`
                procedure_code,
                procedure_description,
                avg_gross_charge,
                min_gross_charge,
                max_gross_charge,
                total_hospitals,
                standard_unit,
                avg_price_per_unit
              `)
              .eq('procedure_code', upperQuery)
              .limit(10);

            if (exactAvgData && exactAvgData.length > 0) {
              avgData = exactAvgData;
            } else if (query.length >= 3) {
              // Fall back to LIKE search for longer queries
              const { data: likeAvgData, error: likeAvgError } = await supabase
                .from('procedure_pricing_averages')
                .select(`
                  procedure_code,
                  procedure_description,
                  avg_gross_charge,
                  min_gross_charge,
                  max_gross_charge,
                  total_hospitals,
                  standard_unit,
                  avg_price_per_unit
                `)
                .or(`procedure_code.ilike.%${upperQuery}%,procedure_description.ilike.%${query}%`)
                .limit(10);

              avgData = likeAvgData;
              avgError = likeAvgError;
            }

            if (avgError) {
              console.warn('Error fetching procedure pricing averages:', avgError);
              return; // Skip this section if there's an error
            }

          if (avgData && avgData.length > 0) {
            // Get consumer-friendly names from procedures table for matching codes
            let consumerFriendlyMap = new Map();
            try {
              const codes = avgData.map(item => item.procedure_code);
              const { data: procedureData } = await supabase
                .from('procedures')
                .select('code, consumer_friendly_description')
                .in('code', codes);

              if (procedureData) {
                procedureData.forEach(proc => {
                  if (proc.consumer_friendly_description) {
                    consumerFriendlyMap.set(proc.code, proc.consumer_friendly_description);
                  }
                });
              }
            } catch (error) {
              console.warn('Failed to fetch consumer-friendly names:', error);
            }

            // Add these results to the existing results, avoiding duplicates
            const existingCodes = new Set(results.map(r => r.code));
            avgData.forEach(item => {
              if (!existingCodes.has(item.procedure_code)) {
                results.push({
                  id: item.procedure_code,
                  code: item.procedure_code,
                  codeType: 'CPT' as const,
                  description: item.procedure_description || '',
                  consumerFriendlyName: consumerFriendlyMap.get(item.procedure_code),
                  matchCount: 1,
                  avgPrice: item.avg_gross_charge || undefined,
                  minPrice: item.min_gross_charge || undefined,
                  maxPrice: item.max_gross_charge || undefined,
                  hospitalCount: item.total_hospitals,
                  source: 'Procedure Pricing',
                  standardUnit: item.standard_unit || undefined,
                  avgPricePerUnit: item.avg_price_per_unit || undefined,
                  priceSpread: item.min_gross_charge && item.max_gross_charge && item.min_gross_charge > 0
                    ? ((item.max_gross_charge - item.min_gross_charge) / item.min_gross_charge) * 100
                    : undefined
                });
              }
            });
          }
          } catch (cptError) {
            console.warn('Error in CPT search section:', cptError);
          }
        } else if (cat.key === 'supply') {
          const { data } = await supabase
            .from('supply_pricing_averages')
            .select('supply_code, supply_name, avg_charge, total_hospitals, standard_unit, avg_price_per_unit')
            .or(`supply_code.ilike.%${upperQuery}%,supply_name.ilike.%${query}%`)
            .limit(10);

          if (data) {
            results.push(...data.map(item => ({
              id: item.supply_code,
              code: item.supply_code,
              codeType: 'SUPPLY' as const,
              description: item.supply_name || '',
              matchCount: 1,
              avgPrice: item.avg_charge || undefined,
              hospitalCount: item.total_hospitals,
              source: 'Supply Pricing',
              standardUnit: item.standard_unit || undefined,
              avgPricePerUnit: item.avg_price_per_unit || undefined
            })));
          }
        } else if (cat.key === 'drug') {
          const { data } = await supabase
            .from('drug_pricing_averages')
            .select('drug_code, drug_name, avg_charge, total_hospitals, standard_unit, avg_price_per_unit')
            .or(`drug_code.ilike.%${upperQuery}%,drug_name.ilike.%${query}%`)
            .limit(10);

          if (data) {
            results.push(...data.map(item => ({
              id: item.drug_code,
              code: item.drug_code,
              codeType: 'DRUG' as const,
              description: item.drug_name || '',
              matchCount: 1,
              avgPrice: item.avg_charge || undefined,
              hospitalCount: item.total_hospitals,
              source: 'Drug Pricing',
              standardUnit: item.standard_unit || undefined,
              avgPricePerUnit: item.avg_price_per_unit || undefined
            })));
          }
        }

        // Fetch proper descriptions and consumer-friendly names from procedures table
        if (results.length > 0 && (cat.key === 'cpt' || cat.key === 'hcpcs' || cat.key === 'drg' || cat.key === 'rev')) {
          try {
            const codes = results.map(r => r.code);
            const { data: procedureData } = await supabase
              .from('procedures')
              .select('code, name, description, consumer_friendly_description')
              .in('code', codes);

            if (procedureData) {
              const procedureMap = new Map();
              procedureData.forEach(proc => {
                procedureMap.set(proc.code, {
                  name: proc.name,
                  description: proc.description,
                  consumerFriendlyName: proc.consumer_friendly_description
                });
              });

              // Update results with proper descriptions
              results = results.map(result => {
                const procData = procedureMap.get(result.code);
                if (procData) {
                  return {
                    ...result,
                    description: procData.description || procData.name || result.description,
                    consumerFriendlyName: procData.consumerFriendlyName || result.consumerFriendlyName
                  };
                }
                return result;
              });
            }
          } catch (error) {
            console.warn('Failed to fetch procedure descriptions:', error);
          }
        }

        // Update the specific category match
        setCategoryMatches(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            matchCount: results.length,
            results: results,
            loading: false
          };
          return updated;
        });
      } catch (error) {
        // Update with error
        setCategoryMatches(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            loading: false,
            error: t('errors.categorySearchFailed')
          };
          return updated;
        });
      }
    });

    await Promise.all(searchPromises);
  };

  const handleSearch = (query: string, type: 'hospital' | 'procedure') => {
    trackSearch(query, type);
    // Only update search type if it's different
    if (type !== searchType) {
      setSearchType(type);
    }
    // Only update query if it's different to avoid re-triggering search
    if (query !== searchQuery) {
      setSearchQuery(query);
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return t('priceCard.notAvailable');
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const totalMatches = categoryMatches.reduce((sum, cat) => sum + cat.matchCount, 0);
  const isSearching = categoryMatches.some(cat => cat.loading);
  
  // Fetch all hospitals ordered by markup percentage for default display
  const { data: allHospitals = [], isLoading: isLoadingAllHospitals } = useQuery({
    queryKey: ['all-hospitals-by-markup', currentPage],
    queryFn: async () => {
      const startRange = (currentPage - 1) * ITEMS_PER_PAGE;
      const endRange = startRange + ITEMS_PER_PAGE - 1;
      
      // Get rankings data first
      const { data: rankingsData, error: rankingsError } = await supabase
        .from('hospital_ranking_summary')
        .select(`
          hospital_id, 
          avg_gross_markup_percentage, 
          median_gross_markup_percentage,
          shame_score_calculated, 
          total_procedures,
          expensive_procedures_count,
          outlier_expensive_count,
          cpt_avg_markup_percentage,
          drg_avg_markup_percentage,
          rev_avg_markup_percentage,
          ndc_avg_markup_percentage,
          cpt_procedure_count,
          drg_procedure_count,
          rev_procedure_count,
          ndc_procedure_count
        `)
        .not('avg_gross_markup_percentage', 'is', null)
        .order('median_gross_markup_percentage', { ascending: false, nullsFirst: false })
        .range(startRange, endRange);
        
      if (rankingsError) throw rankingsError;
      if (!rankingsData || rankingsData.length === 0) return [];

      // Get hospital details
      const hospitalIds = rankingsData.map(r => r.hospital_id);
      const { data: hospitalsData, error: hospitalsError } = await supabase
        .from('hospitals')
        .select('*')
        .in('id', hospitalIds);
        
      if (hospitalsError) throw hospitalsError;
      if (!hospitalsData) return [];

      // Merge hospital data with ranking data, preserving ranking order and adding shame rank
      return rankingsData.map((ranking, index) => {
        const hospital = hospitalsData.find(h => h.id === ranking.hospital_id);
        if (!hospital) return null;
        
        // Calculate global ranking position (1-based)
        const globalRank = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        
        return {
          ...hospital,
          avg_gross_markup_percentage: ranking.avg_gross_markup_percentage,
          median_gross_markup_percentage: ranking.median_gross_markup_percentage,
          shame_score_calculated: ranking.shame_score_calculated,
          total_procedures_calculated: ranking.total_procedures,
          expensive_procedures_count: ranking.expensive_procedures_count,
          outlier_expensive_count: ranking.outlier_expensive_count,
          shame_rank: globalRank,
          // Add category-specific markups
          cpt_avg_markup_percentage: ranking.cpt_avg_markup_percentage,
          drg_avg_markup_percentage: ranking.drg_avg_markup_percentage,
          rev_avg_markup_percentage: ranking.rev_avg_markup_percentage,
          ndc_avg_markup_percentage: ranking.ndc_avg_markup_percentage,
          cpt_procedure_count: ranking.cpt_procedure_count,
          drg_procedure_count: ranking.drg_procedure_count,
          rev_procedure_count: ranking.rev_procedure_count,
          ndc_procedure_count: ranking.ndc_procedure_count,
        };
      }).filter(Boolean);
    },
    enabled: !searchQuery && searchType === 'hospital'
  });
  
  // Get total count of hospitals with ranking data
  const { data: hospitalCount } = useQuery({
    queryKey: ['hospital-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('hospital_ranking_summary')
        .select('*', { count: 'exact', head: true })
        .not('avg_gross_markup_percentage', 'is', null);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !searchQuery && searchType === 'hospital'
  });
  
  const totalPages = hospitalCount ? Math.ceil(hospitalCount / ITEMS_PER_PAGE) : 1;

  const brand = useBrand();

  return (
    <div className={`min-h-screen pb-20 ${brand.key === 'antigreed' ? 'bg-gradient-to-br from-amber-50 to-orange-50' : 'bg-background'}`}>
      <Helmet>
        <title>{t('hero.title')} | {brand.name}</title>
        <meta name="description" content={t('seo.description')} />
        <meta name="keywords" content="search hospital prices, compare hospital costs, find hospital prices, hospital price search, medical procedure costs, hospital near me prices" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/search`} />
        <meta property="og:title" content={t('hero.title')} />
        <meta property="og:description" content={t('seo.description')} />
        <meta property="og:image" content={brand.key === 'ahp' ? 'https://americanhospitalprices.com/consumer/og-search.png' : brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/search`} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('hero.title')} />
        <meta name="twitter:description" content={t('seo.description')} />
        <meta name="twitter:image" content={brand.key === 'ahp' ? 'https://americanhospitalprices.com/consumer/og-search.png' : brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": brand.name,
          "url": brand.primaryDomain,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${brand.primaryDomain}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}</script>
      </Helmet>
      {/* Header with Search */}
      <div className={`${brand.key === 'antigreed' ? 'bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-b-2 border-amber-200 shadow-lg' : 'bg-secondary border-b border-border'}`}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center mb-6">
            <h1 className={`text-4xl font-bold ${brand.key === 'antigreed' ? 'font-serif text-emerald-800' : 'text-foreground'}`}>
              {t('hero.dataVaultTitle')}
            </h1>
          </div>
          
          {/* Search Bar */}
          <div className="mb-6">
            <SearchBarWithAutocomplete
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onSearch={handleSearch}
              placeholder={searchType === 'hospital'
                ? t('hero.hospitalPlaceholder')
                : t('hero.procedurePlaceholder')}
              autoFocus={true}
              initialSearchType={searchType}
            />
          </div>

          {/* Search Type Tabs */}
          <div className="flex justify-center mb-6">
            <div className={`${brand.key === 'antigreed' ? 'bg-gradient-to-br from-emerald-50/80 to-amber-50/70 border border-emerald-200' : 'bg-background/50'} rounded-lg p-1 inline-flex`}>
              <Button
                variant={searchType === 'hospital' ? 'default' : 'ghost'}
                onClick={() => {
                  setSearchType('hospital');
                  if (searchQuery) searchHospitals(searchQuery);
                }}
                className={`font-mono ${brand.key === 'antigreed' && searchType === 'hospital' ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : brand.key === 'antigreed' ? 'text-gray-700 hover:bg-amber-50' : ''}`}
              >
                <Building2 className="w-4 h-4 mr-2" />
                {t('results.hospitals')}
              </Button>
              <Button
                variant={searchType === 'procedure' ? 'default' : 'ghost'}
                onClick={() => {
                  setSearchType('procedure');
                  if (searchQuery) performComprehensiveSearch(searchQuery);
                }}
                className={`font-mono ${brand.key === 'antigreed' && searchType === 'procedure' ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : brand.key === 'antigreed' ? 'text-gray-700 hover:bg-amber-50' : ''}`}
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                {t('results.procedures')}
              </Button>
            </div>
          </div>

          {/* Display Current Search Query Prominently */}
          {searchQuery && (
            <div
              className={`${brand.key === 'antigreed' ? 'bg-gradient-to-br from-emerald-50/80 to-amber-50/70 border border-emerald-200 hover:border-emerald-300' : 'bg-background/50 border border-border hover:border-primary/50'} rounded-lg p-4 cursor-pointer transition-colors`}
              onClick={() => {
                // Scroll to top and focus the search input
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
                  if (searchInput) {
                    searchInput.focus();
                    searchInput.select();
                  }
                }, 100);
              }}
            >
              <div className="flex items-center gap-3">
                <Search className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className={`text-sm ${brand.key === 'antigreed' ? 'text-gray-600' : 'text-muted-foreground'}`}>{t('results.currentSearch')}</p>
                  <p className={`text-2xl font-bold font-mono ${brand.key === 'antigreed' ? 'text-gray-900' : 'text-foreground'}`}>{searchQuery}</p>
                </div>
                {searchType === 'procedure' && !isSearching && totalMatches > 0 && (
                  <div className="ml-auto text-right">
                    <p className="text-sm text-muted-foreground">{t('results.totalMatches')}</p>
                    <p className="text-2xl font-bold text-primary">{totalMatches}</p>
                  </div>
                )}
                {searchType === 'hospital' && !isSearchingHospitals && hospitalResults.length > 0 && (
                  <div className="ml-auto text-right">
                    <p className="text-sm text-muted-foreground">{t('results.hospitalsFound')}</p>
                    <p className="text-2xl font-bold text-primary">{hospitalResults.length}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {!searchQuery && searchType === 'hospital' ? (
          // Default view - show 25 worst hospitals using search results styling
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className={`text-3xl font-bold ${brand.key === 'antigreed' ? 'font-serif text-emerald-800' : 'text-foreground'} mb-4`}>
                {t('offenders.title')}
              </h2>
              <p className={`${brand.key === 'antigreed' ? 'text-gray-700 font-medium' : 'text-muted-foreground'} mb-4`}>
                {t('offenders.description')}
              </p>
              <div className="flex justify-center">
                <Link to="/hospital-rankings">
                  <Button
                    variant="outline"
                    className={`flex items-center gap-2 ${brand.key === 'antigreed' ? 'border-emerald-700 text-emerald-700 hover:bg-emerald-50 bg-white' : ''}`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    {t('offenders.viewRankings')}
                  </Button>
                </Link>
              </div>
            </div>
            
            {isLoadingAllHospitals ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : allHospitals.length > 0 ? (
              <div className="grid gap-4">
                {allHospitals.map((hospital, index) => (
                  <Card
                    key={hospital.id}
                    className={`${brand.key === 'antigreed' ? 'border-emerald-400 hover:border-emerald-500 hover:shadow-lg' : 'bg-black/80 border-destructive/20 hover:border-destructive/40 hover:shadow-lg hover:shadow-destructive/20'} transition-all duration-300`}
                    style={brand.key === 'antigreed' ? { background: 'linear-gradient(358deg, #09291e, #165a45)' } : undefined}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={`font-mono text-sm font-bold ${brand.key === 'antigreed' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-destructive/20 text-destructive border-destructive'}`}>
                                #{hospital.shame_rank || (index + 1)}
                              </Badge>
                              <Building2 className={`w-5 h-5 ${brand.key === 'antigreed' ? 'text-amber-300' : 'text-primary'}`} />
                            </div>
                            <h3
                              className={`text-xl font-bold cursor-pointer transition-colors duration-200 ${brand.key === 'antigreed' ? 'text-amber-50 hover:text-white' : 'hover:text-primary'}`}
                              onClick={() => { trackSearchResultClick('hospital', hospital.id, hospital.name); navigate(`/hospital/${hospital.id}`); }}
                            >
                              {hospital.name}
                            </h3>
                            {hospital.shame_score && hospital.shame_score > 50 && (
                              <Badge variant="destructive" className="font-mono">
                                {t('hospitalCard.shameScore', { score: hospital.shame_score })}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {hospital.city}, {hospital.state}
                            </div>
                            {hospital.total_procedures && (
                              <div className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                {t('hospitalCard.procedures', { value: hospital.total_procedures.toLocaleString() })}
                              </div>
                            )}
                            {hospital.outlier_expensive_count !== undefined && hospital.total_procedures && (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4 text-red-400" />
                                <span className="text-red-400 font-semibold">
                                  {t('hospitalCard.outliers', { value: hospital.outlier_expensive_count.toLocaleString() })}
                                </span>
                                <span className="text-muted-foreground">
                                  ({((hospital.outlier_expensive_count / hospital.total_procedures) * 100).toFixed(1)}%)
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4">
                            {(hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage) != null && (() => {
                              const m = hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage;
                              return (
                              <div className={`px-3 py-1 rounded-full text-xs font-mono ${
                                m > 175 ? 'bg-red-500/20 text-red-400' :
                                m > 90 ? 'bg-orange-500/20 text-orange-400' :
                                m > 20 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-green-500/20 text-green-400'
                              }`}>
                                {t('hospitalCard.overallMarkup', { value: `${m >= 0 ? '+' : ''}${m.toFixed(1)}` })}
                              </div>
                              );
                            })()}
                            
                            {/* Category-specific markups - always show all categories */}
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-blue-500/10 text-blue-400 cursor-help">
                                {t('categories.cpt')}: {hospital.cpt_avg_markup_percentage !== null && hospital.cpt_avg_markup_percentage !== undefined ? 
                                  `${hospital.cpt_avg_markup_percentage >= 0 ? '+' : ''}${hospital.cpt_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.cpt_procedure_count > 0 && ` (${hospital.cpt_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.cpt_avg_markup_percentage !== null && hospital.cpt_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.cpt_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.cpt_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.cpt_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-purple-500/10 text-purple-400 cursor-help">
                                {t('categories.drg')}: {hospital.drg_avg_markup_percentage !== null && hospital.drg_avg_markup_percentage !== undefined ? 
                                  `${hospital.drg_avg_markup_percentage >= 0 ? '+' : ''}${hospital.drg_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.drg_procedure_count > 0 && ` (${hospital.drg_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.drg_avg_markup_percentage !== null && hospital.drg_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.drg_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.drg_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.drg_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-green-500/10 text-green-400 cursor-help">
                                {t('categories.rev')}: {hospital.rev_avg_markup_percentage !== null && hospital.rev_avg_markup_percentage !== undefined ? 
                                  `${hospital.rev_avg_markup_percentage >= 0 ? '+' : ''}${hospital.rev_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.rev_procedure_count > 0 && ` (${hospital.rev_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.rev_avg_markup_percentage !== null && hospital.rev_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.rev_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.rev_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.rev_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-orange-500/10 text-orange-400 cursor-help">
                                {t('categories.ndc')}: {hospital.ndc_avg_markup_percentage !== null && hospital.ndc_avg_markup_percentage !== undefined ? 
                                  `${hospital.ndc_avg_markup_percentage >= 0 ? '+' : ''}${hospital.ndc_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.ndc_procedure_count > 0 && ` (${hospital.ndc_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.ndc_avg_markup_percentage !== null && hospital.ndc_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.ndc_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.ndc_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.ndc_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                          </div>
                          
                          {/* Procedures Carousel */}
                          <div className="mt-4 pt-4 border-t border-border/30">
                            <SearchResultsProceduresCarousel 
                              hospitalId={hospital.id} 
                              hospitalName={hospital.name}
                            />
                          </div>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-muted-foreground mt-2" />
                      </div>
                    </CardContent>
                  </Card>
                ))}

              <div className="flex justify-center items-center space-x-4 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoadingAllHospitals}
                  className={brand.key === 'antigreed' ? 'border-emerald-700 text-emerald-700 hover:bg-emerald-50 bg-white' : ''}
                >
                  {t('pagination.previous')}
                </Button>
                <span className={`font-mono ${brand.key === 'antigreed' ? 'text-gray-700' : 'text-muted-foreground'}`}>
                  {t('pagination.pageOf', { current: currentPage, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages || isLoadingAllHospitals}
                  className={brand.key === 'antigreed' ? 'border-emerald-700 text-emerald-700 hover:bg-emerald-50 bg-white' : ''}
                >
                  {t('pagination.next')}
                </Button>
              </div>
              </div>
            ) : (
              <Card
                className={`text-center p-12 ${brand.key === 'antigreed' ? 'border-emerald-400' : ''}`}
                style={brand.key === 'antigreed' ? { background: 'linear-gradient(358deg, #09291e, #165a45)' } : undefined}
              >
                <Building2 className={`w-16 h-16 mx-auto mb-4 ${brand.key === 'antigreed' ? 'text-amber-300' : 'text-muted-foreground'}`} />
                <h3 className={`text-xl font-bold mb-2 ${brand.key === 'antigreed' ? 'text-amber-100' : ''}`}>{t('emptyStates.noHospitalDataTitle')}</h3>
                <p className={`${brand.key === 'antigreed' ? 'text-emerald-100' : 'text-muted-foreground'} max-w-md mx-auto`}>
                  {t('emptyStates.noHospitalDataBody')}
                </p>
              </Card>
            )}
          </div>
        ) : !searchQuery ? (
          // Initial state for procedure search - prompt to search
          <Card
            className={`text-center p-12 ${brand.key === 'antigreed' ? 'border-emerald-400' : ''}`}
            style={brand.key === 'antigreed' ? { background: 'linear-gradient(358deg, #09291e, #165a45)' } : undefined}
          >
            <Search className={`w-16 h-16 mx-auto mb-4 ${brand.key === 'antigreed' ? 'text-amber-300' : 'text-muted-foreground'}`} />
            <h2 className={`text-2xl font-bold mb-2 ${brand.key === 'antigreed' ? 'text-amber-100' : ''}`}>{t('emptyStates.startSearchTitle')}</h2>
            <p className={`${brand.key === 'antigreed' ? 'text-emerald-100' : 'text-muted-foreground'} max-w-md mx-auto`}>
              {t('emptyStates.startSearchBody')}
            </p>
          </Card>
        ) : searchType === 'hospital' ? (
          // Hospital search results
          <div className="space-y-6">
            {isSearchingHospitals ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : hospitalResults.length > 0 ? (
              <div className="grid gap-4">
                {hospitalResults.map((hospital, index) => (
                  <Card
                    key={hospital.id}
                    className={`${brand.key === 'antigreed' ? 'border-emerald-400 hover:border-emerald-500 hover:shadow-lg' : 'bg-black/80 border-destructive/20 hover:border-destructive/40 hover:shadow-lg hover:shadow-destructive/20'} transition-all duration-300`}
                    style={brand.key === 'antigreed' ? { background: 'linear-gradient(358deg, #09291e, #165a45)' } : undefined}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={`font-mono text-sm font-bold ${brand.key === 'antigreed' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-destructive/20 text-destructive border-destructive'}`}>
                                #{hospital.shame_rank || (index + 1)}
                              </Badge>
                              <Building2 className={`w-5 h-5 ${brand.key === 'antigreed' ? 'text-amber-300' : 'text-primary'}`} />
                            </div>
                            <h3
                              className={`text-xl font-bold cursor-pointer transition-colors duration-200 ${brand.key === 'antigreed' ? 'text-amber-50 hover:text-white' : 'hover:text-primary'}`}
                              onClick={() => { trackSearchResultClick('hospital', hospital.id, hospital.name); navigate(`/hospital/${hospital.id}`); }}
                            >
                              {hospital.name}
                            </h3>
                            {hospital.shame_score && hospital.shame_score > 50 && (
                              <Badge variant="destructive" className="font-mono">
                                {t('hospitalCard.shameScore', { score: hospital.shame_score })}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {hospital.city}, {hospital.state}
                            </div>
                            {hospital.total_procedures && (
                              <div className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                {t('hospitalCard.procedures', { value: hospital.total_procedures.toLocaleString() })}
                              </div>
                            )}
                            {hospital.outlier_expensive_count !== undefined && hospital.total_procedures && (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4 text-red-400" />
                                <span className="text-red-400 font-semibold">
                                  {t('hospitalCard.outliers', { value: hospital.outlier_expensive_count.toLocaleString() })}
                                </span>
                                <span className="text-muted-foreground">
                                  ({((hospital.outlier_expensive_count / hospital.total_procedures) * 100).toFixed(1)}%)
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4">
                            {(hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage) != null && (() => {
                              const m = hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage;
                              return (
                              <div className={`px-3 py-1 rounded-full text-xs font-mono ${
                                m > 175 ? 'bg-red-500/20 text-red-400' :
                                m > 90 ? 'bg-orange-500/20 text-orange-400' :
                                m > 20 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-green-500/20 text-green-400'
                              }`}>
                                {t('hospitalCard.overallMarkup', { value: `${m >= 0 ? '+' : ''}${m.toFixed(1)}` })}
                              </div>
                              );
                            })()}
                            
                            {/* Category-specific markups - always show all categories */}
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-blue-500/10 text-blue-400 cursor-help">
                                {t('categories.cpt')}: {hospital.cpt_avg_markup_percentage !== null && hospital.cpt_avg_markup_percentage !== undefined ? 
                                  `${hospital.cpt_avg_markup_percentage >= 0 ? '+' : ''}${hospital.cpt_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.cpt_procedure_count > 0 && ` (${hospital.cpt_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.cpt_avg_markup_percentage !== null && hospital.cpt_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.cpt_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.cpt_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.cpt_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-purple-500/10 text-purple-400 cursor-help">
                                {t('categories.drg')}: {hospital.drg_avg_markup_percentage !== null && hospital.drg_avg_markup_percentage !== undefined ? 
                                  `${hospital.drg_avg_markup_percentage >= 0 ? '+' : ''}${hospital.drg_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.drg_procedure_count > 0 && ` (${hospital.drg_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.drg_avg_markup_percentage !== null && hospital.drg_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.drg_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.drg_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.drg_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-green-500/10 text-green-400 cursor-help">
                                {t('categories.rev')}: {hospital.rev_avg_markup_percentage !== null && hospital.rev_avg_markup_percentage !== undefined ? 
                                  `${hospital.rev_avg_markup_percentage >= 0 ? '+' : ''}${hospital.rev_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.rev_procedure_count > 0 && ` (${hospital.rev_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.rev_avg_markup_percentage !== null && hospital.rev_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.rev_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.rev_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.rev_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <div className="px-2 py-1 rounded text-xs font-mono bg-orange-500/10 text-orange-400 cursor-help">
                                {t('categories.ndc')}: {hospital.ndc_avg_markup_percentage !== null && hospital.ndc_avg_markup_percentage !== undefined ? 
                                  `${hospital.ndc_avg_markup_percentage >= 0 ? '+' : ''}${hospital.ndc_avg_markup_percentage.toFixed(0)}%` : '—'}
                                {hospital.ndc_procedure_count > 0 && ` (${hospital.ndc_procedure_count.toLocaleString()})`}
                              </div>
                              {hospital.ndc_avg_markup_percentage !== null && hospital.ndc_avg_markup_percentage !== undefined && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-gray-700">
                                  {hospital.ndc_avg_markup_percentage < 0 ?
                                    t('hospitalCard.cheaperThanAverage', { value: Math.abs(hospital.ndc_avg_markup_percentage).toFixed(0) }) :
                                    t('hospitalCard.moreExpensiveThanAverage', { value: hospital.ndc_avg_markup_percentage.toFixed(0) })}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                          </div>
                          
                          {/* Procedures Carousel */}
                          <div className="mt-4 pt-4 border-t border-border/30">
                            <SearchResultsProceduresCarousel 
                              hospitalId={hospital.id} 
                              hospitalName={hospital.name}
                            />
                          </div>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-muted-foreground mt-2" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card
                className={`text-center p-12 ${brand.key === 'antigreed' ? 'border-emerald-400' : ''}`}
                style={brand.key === 'antigreed' ? { background: 'linear-gradient(358deg, #09291e, #165a45)' } : undefined}
              >
                <Building2 className={`w-16 h-16 mx-auto mb-4 ${brand.key === 'antigreed' ? 'text-amber-300' : 'text-muted-foreground'}`} />
                <h3 className={`text-xl font-bold mb-2 ${brand.key === 'antigreed' ? 'text-amber-100' : ''}`}>{t('emptyStates.noHospitalsTitle')}</h3>
                <p className={`${brand.key === 'antigreed' ? 'text-emerald-100' : 'text-muted-foreground'} max-w-md mx-auto`}>
                  {t('emptyStates.noHospitalsBody', { query: searchQuery })}
                </p>
                {(/^\d{2,5}$/.test(searchQuery.trim()) ||
                  /^[A-Z]\d{3,4}$/i.test(searchQuery.trim()) ||
                  /^MS-?\d{3}$/i.test(searchQuery.trim())) && (
                  <Button className="mt-5 font-mono" onClick={() => setSearchType('procedure')}>
                    <Stethoscope className="w-4 h-4 mr-2" />
                    {t('emptyStates.searchCodesFor', { query: searchQuery.trim() })}
                  </Button>
                )}
              </Card>
            )}
          </div>
        ) : (
          // Procedure search results with categories
          <div className="space-y-6">
            {/* Category Overview Cards */}
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categoryMatches.map((category) => {
                const IconComponent = category.icon;
                return (
                  <Card 
                    key={category.category}
                    className={`cursor-pointer transition-all ${
                      selectedCategory === category.category 
                        ? 'ring-2 ring-primary shadow-lg' 
                        : 'hover:shadow-md'
                    } ${category.matchCount === 0 && !category.loading ? 'opacity-50' : ''}`}
                    onClick={() => category.matchCount > 0 && setSelectedCategory(
                      selectedCategory === category.category ? null : category.category
                    )}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className={`w-10 h-10 ${category.color} rounded-full flex items-center justify-center`}>
                          {category.loading ? (
                            <Loader2 className="w-5 h-5 text-white animate-spin" />
                          ) : (
                            <IconComponent className="w-5 h-5 text-white" />
                          )}
                        </div>
                        {category.matchCount > 0 && (
                          <Badge variant="secondary" className="font-mono">
                            {category.matchCount}
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-sm mt-2">{category.displayName}</CardTitle>
                    </CardHeader>
                    {category.error && (
                      <CardContent className="pt-0">
                        <p className="text-xs text-destructive">{category.error}</p>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Detailed Results */}
            {selectedCategory ? (
              // Show results for selected category
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className={`text-xl font-bold ${brand.key === 'antigreed' ? 'text-gray-900' : ''}`}>
                    {t('categories.categoryResults', { category: categoryMatches.find(c => c.category === selectedCategory)?.displayName })}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCategory(null)}
                  >
                    {t('categories.showAll')}
                  </Button>
                </div>
                
                {categoryMatches
                  .find(c => c.category === selectedCategory)
                  ?.results.map((result) => (
                    <Card key={`${result.source}-${result.id}`} className={`${brand.key === 'antigreed' ? 'bg-white/90 border-amber-200 hover:border-amber-300' : ''} hover:shadow-md transition-shadow`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <Badge variant="secondary" className="font-mono">
                                {result.codeType} {result.code}
                              </Badge>
                              {result.hospitalCount > 0 && (
                                <span className="text-sm text-muted-foreground">
                                  {t('priceCard.foundInHospitals', { count: result.hospitalCount })}
                                </span>
                              )}
                            </div>
                            
                            <div className="mb-3">
                              {result.consumerFriendlyName && (
                                <h4 className="text-lg font-semibold text-primary mb-1">
                                  {result.consumerFriendlyName}
                                </h4>
                              )}
                              <p className={`${result.consumerFriendlyName ? 'text-sm text-muted-foreground italic' : 'text-lg font-semibold'}`}>
                                "{result.description || t('priceCard.noDescription')}"
                              </p>
                            </div>
                            
                            {result.avgPrice && (
                              <div className="space-y-4 p-4 bg-secondary/30 rounded-lg">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* Average Price */}
                                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <div className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                                      {t('priceCard.average')}
                                    </div>
                                    <div className="text-lg font-bold text-blue-700 dark:text-blue-300 font-mono">
                                      {formatPrice(result.avgPrice)}
                                    </div>
                                  </div>

                                  {/* Low Price */}
                                  {result.minPrice && (
                                    <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                      <div className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">
                                        {t('priceCard.low')}
                                      </div>
                                      <div className="text-lg font-bold text-green-700 dark:text-green-300 font-mono">
                                        {formatPrice(result.minPrice)}
                                      </div>
                                      {result.minHospitalName && (
                                        <div className="text-xs text-green-600 dark:text-green-400 mt-1 truncate">
                                          {t('priceCard.atHospital', { name: result.minHospitalName })}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* High Price */}
                                  {result.maxPrice && (
                                    <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                      <div className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">
                                        {t('priceCard.high')}
                                      </div>
                                      <div className="text-lg font-bold text-red-700 dark:text-red-300 font-mono">
                                        {formatPrice(result.maxPrice)}
                                      </div>
                                      {result.maxHospitalName && (
                                        <div className="text-xs text-red-600 dark:text-red-400 mt-1 truncate">
                                          {t('priceCard.atHospital', { name: result.maxHospitalName })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Price Spread Badge */}
                                {result.priceSpread !== undefined && result.priceSpread > 0 && (
                                  <div className="flex justify-center">
                                    <Badge
                                      variant="outline"
                                      className={`text-sm font-mono ${
                                        result.priceSpread > 200 ? 'border-red-500 text-red-500 bg-red-50 dark:bg-red-900/20' :
                                        result.priceSpread > 100 ? 'border-orange-500 text-orange-500 bg-orange-50 dark:bg-orange-900/20' :
                                        result.priceSpread > 50 ? 'border-yellow-500 text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
                                        'border-green-500 text-green-500 bg-green-50 dark:bg-green-900/20'
                                      }`}
                                    >
                                      {t('priceCard.priceSpread', { value: result.priceSpread.toFixed(0) })}
                                    </Badge>
                                  </div>
                                )}

                                {/* Unit Information */}
                                {result.standardUnit && (
                                  <div className="flex items-center justify-center gap-4 text-sm pt-2 border-t border-border/50">
                                    <Badge variant="outline" className="text-xs bg-secondary">
                                      {t('priceCard.unit', { unit: result.standardUnit })}
                                    </Badge>
                                    {result.avgPricePerUnit && (
                                      <span className="text-muted-foreground font-mono">
                                        {t('priceCard.perUnit', { price: formatPrice(result.avgPricePerUnit), unit: result.standardUnit })}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700"
                            onClick={() => {
                              // Navigate to the appropriate page based on code type
                              const codeType = result.codeType?.toLowerCase();
                              let url = '';
                              if (codeType === 'cpt' || codeType === 'hcpcs') {
                                url = `/cpt/${result.code}`;
                              } else if (codeType === 'drg') {
                                url = `/drg/${result.code}`;
                              } else if (codeType === 'rev' || codeType === 'revenue') {
                                url = `/rev/${result.code}`;
                              } else if (codeType === 'ndc') {
                                url = `/ndc/${result.code}`;
                              } else if (codeType === 'sup' || codeType === 'supply') {
                                url = `/sup/${result.code}`;
                              } else if (codeType === 'rx' || codeType === 'drug') {
                                url = `/rx/${result.code}`;
                              } else {
                                // Fallback to search if type is unknown
                                url = `/search?q=${encodeURIComponent(result.code)}&type=procedure`;
                              }
                              navigate(url);
                            }}
                          >
                            <div className="w-4 h-4 mr-1 bg-blue-600 rounded-sm border border-blue-700 flex items-center justify-center relative overflow-hidden">
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 opacity-20"></div>
                            </div>
                            {t('actions.viewPrices')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            ) : (
              // Show all categories with results
              <div className="space-y-8">
                {categoryMatches
                  .filter(cat => cat.matchCount > 0)
                  .map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <div key={category.category} className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${category.color} rounded-full flex items-center justify-center`}>
                            <IconComponent className="w-4 h-4 text-white" />
                          </div>
                          <h3 className={`text-xl font-bold ${brand.key === 'antigreed' ? 'text-gray-900' : ''}`}>{category.displayName}</h3>
                          <Badge variant="outline">{t('matchesBadge', { count: category.matchCount })}</Badge>
                        </div>
                        
                        <div className="grid gap-3">
                          {category.results.slice(0, 3).map((result) => (
                            <Card
                              key={`${result.source}-${result.id}`}
                              className={`${brand.key === 'antigreed' ? 'bg-white/90 border-amber-200 hover:border-amber-300' : 'hover:border-primary'} hover:shadow-md transition-shadow cursor-pointer`}
                              onClick={() => {
                                // Navigate to the appropriate page based on code type
                                const codeType = result.codeType?.toLowerCase();
                                let url = '';
                                if (codeType === 'cpt' || codeType === 'hcpcs') {
                                  url = `/cpt/${result.code}`;
                                } else if (codeType === 'drg') {
                                  url = `/drg/${result.code}`;
                                } else if (codeType === 'rev' || codeType === 'revenue') {
                                  url = `/rev/${result.code}`;
                                } else if (codeType === 'ndc') {
                                  url = `/ndc/${result.code}`;
                                } else if (codeType === 'sup' || codeType === 'supply') {
                                  url = `/sup/${result.code}`;
                                } else if (codeType === 'rx' || codeType === 'drug') {
                                  url = `/rx/${result.code}`;
                                } else {
                                  // Fallback to search if type is unknown
                                  url = `/search?q=${encodeURIComponent(result.code)}&type=procedure`;
                                }
                                navigate(url);
                              }}
                            >
                              <CardContent className="p-4">
                                <div className="flex flex-col gap-4">
                                  {/* Header with code and hospital count */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <Badge variant="secondary" className="font-mono text-sm font-bold px-3 py-1">
                                        {result.code}
                                      </Badge>
                                      {result.hospitalCount > 0 && (
                                        <Badge variant="outline" className="text-xs bg-gray-800 border-gray-700 text-gray-300">
                                          {t('priceCard.hospitalCount', { count: result.hospitalCount })}
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0 border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const codeType = result.codeType?.toLowerCase();
                                      let url = '';
                                      if (codeType === 'cpt' || codeType === 'hcpcs') {
                                        url = `/cpt/${result.code}`;
                                      } else if (codeType === 'drg') {
                                        url = `/drg/${result.code}`;
                                      } else if (codeType === 'rev' || codeType === 'revenue') {
                                        url = `/rev/${result.code}`;
                                      } else if (codeType === 'ndc') {
                                        url = `/ndc/${result.code}`;
                                      } else if (codeType === 'sup' || codeType === 'supply') {
                                        url = `/sup/${result.code}`;
                                      } else if (codeType === 'rx' || codeType === 'drug') {
                                        url = `/rx/${result.code}`;
                                      } else {
                                        url = `/search?q=${encodeURIComponent(result.code)}&type=procedure`;
                                      }
                                      window.open(url, '_blank');
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  </div>

                                  {/* Procedure Names */}
                                  <div className="space-y-1">
                                    {result.consumerFriendlyName ? (
                                      <>
                                        <h3 className="text-lg font-bold text-white leading-tight">
                                          {result.consumerFriendlyName}
                                        </h3>
                                        <p className="text-sm text-muted-foreground italic">
                                          "{result.description || t('priceCard.noDescription')}"
                                        </p>
                                      </>
                                    ) : (
                                      <h3 className="text-lg font-bold text-white leading-tight">
                                        "{result.description || t('priceCard.noDescription')}"
                                      </h3>
                                    )}
                                  </div>

                                  {/* Pricing Information */}
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                      {result.avgPrice && (
                                        <div className="text-2xl font-bold text-primary">
                                          {formatPrice(result.avgPrice)}
                                        </div>
                                      )}
                                      {result.priceSpread !== undefined && result.priceSpread > 0 && (
                                        <Badge
                                          variant="outline"
                                          className={`text-sm font-medium px-3 py-1 ${
                                            result.priceSpread > 200 ? 'border-red-500 text-red-500 bg-red-500/10' :
                                            result.priceSpread > 100 ? 'border-orange-500 text-orange-500 bg-orange-500/10' :
                                            result.priceSpread > 50 ? 'border-yellow-500 text-yellow-500 bg-yellow-500/10' :
                                            'border-green-500 text-green-500 bg-green-500/10'
                                          }`}
                                        >
                                          {t('priceCard.spread', { value: result.priceSpread.toFixed(0) })}
                                        </Badge>
                                      )}
                                      {result.standardUnit && (
                                        <Badge variant="outline" className="text-sm px-3 py-1">
                                          {result.standardUnit}
                                        </Badge>
                                      )}
                                    </div>

                                    {/* Min/Max Prices with Hospital Buttons */}
                                    {result.minPrice && result.maxPrice && result.minPrice !== result.maxPrice && (
                                      <div className="grid grid-cols-2 gap-4">
                                        {/* Low Price */}
                                        <div className="space-y-2">
                                          <div className="text-lg font-bold text-green-400 font-mono">
                                            {formatPrice(result.minPrice)}
                                          </div>
                                          {result.minHospitalName && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="w-full text-left justify-start border-green-500/30 text-green-400 hover:bg-green-500/10 hover:border-green-500/50 text-xs font-medium truncate"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (result.minHospitalId) navigate(`/hospital/${result.minHospitalId}`);
                                              }}
                                            >
                                              {t('priceCard.atHospital', { name: result.minHospitalName })}
                                            </Button>
                                          )}
                                        </div>

                                        {/* High Price */}
                                        <div className="space-y-2">
                                          <div className="text-lg font-bold text-red-400 font-mono text-right">
                                            {formatPrice(result.maxPrice)}
                                          </div>
                                          {result.maxHospitalName && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="w-full text-right justify-end border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 text-xs font-medium truncate"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (result.maxHospitalId) navigate(`/hospital/${result.maxHospitalId}`);
                                              }}
                                            >
                                              {t('priceCard.atHospital', { name: result.maxHospitalName })}
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                          {category.matchCount > 3 && (
                            <Button
                              variant="outline"
                              onClick={() => setSelectedCategory(category.category)}
                              className="w-full"
                            >
                              {t('categories.viewAll', { count: category.matchCount, name: category.displayName })}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                {totalMatches === 0 && !isSearching && (
                  <Card className={`text-center p-12 ${brand.key === 'antigreed' ? 'bg-white/90 border-amber-200' : ''}`}>
                    <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${brand.key === 'antigreed' ? 'text-gray-400' : 'text-muted-foreground'}`} />
                    <h3 className={`text-xl font-bold mb-2 ${brand.key === 'antigreed' ? 'text-gray-900' : ''}`}>{t('emptyStates.noResultsTitle')}</h3>
                    <p className={`${brand.key === 'antigreed' ? 'text-gray-600' : 'text-muted-foreground'} max-w-md mx-auto`}>
                      {t('empty.noResults', { query: searchQuery })} {t('empty.tryDifferent')}
                    </p>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchEnhanced;